import { ResultOf } from "@graphql-typed-document-node/core";
import { uniqBy } from "lodash";
import { RefObject, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "urql";

import { FullPageSpinner } from "@/components/Spinner";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from "@/components/ui/pagination";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { FragmentType, getFragmentData, graphql } from "@/gen";
import { GamesFilterInput, GamesOrderInput } from "@/gen/graphql";

const useGames_Game = graphql(`
    fragment useGames_Game on Game {
        id
        ...GameTable_Game
    }
`);

const GAMES_PAGE_SIZE = 50;

const useGames = ({
    currentPage,
    filters,
    order,
}: {
    currentPage: number;
    filters: GamesFilterInput;
    order: GamesOrderInput;
}) => {
    const [games, setGames] = useState<ResultOf<typeof useGames_Game>[]>([]);

    const offset = currentPage * GAMES_PAGE_SIZE;

    const [result, refetch] = useQuery({
        query: graphql(`
            query gamesQuery($input: GamesInput!) {
                games(input: $input) {
                    items {
                        id
                        ...useGames_Game
                    }
                    total
                    hasMore
                }
            }
        `),
        variables: {
            input: {
                filters,
                limit: 50,
                offset,
                order,
            },
        },
    });

    useEffect(() => {
        if (result.data?.games?.items) {
            const games = getFragmentData(useGames_Game, result.data.games.items);
            setGames(uniqBy(games ?? [], "id"));
        }
    }, [result.data?.games.items]);

    return {
        games,
        hasMore: result.data?.games?.hasMore ?? false,
        gamesCount: result.data?.games?.total ?? null,
        pageCounts: result.data?.games?.total ? Math.ceil(result.data.games.total / 50) : null,
        loading: result.fetching,
        error: result.error,
        refetch,
    };
};

const useGamesParams = () => {
    const [params, setParams] = useSearchParams();

    const updateParam = useCallback(
        (key: string, value?: string, replace?: boolean) => {
            if (value) {
                params.set(key, value);
            } else {
                params.delete(key);
            }
            setParams(params, { replace });
        },
        [params, setParams]
    );

    return {
        pageNumber: parseInt(params.get("page") ?? "0", 10),
        setPageNumber: useCallback(
            (page: number) => updateParam("page", page.toString(), true),
            [updateParam]
        ),
    };
};

const GamesRoute = () => {
    const params = useGamesParams();

    const { games, hasMore, loading, error } = useGames({
        currentPage: params.pageNumber,
        filters: {},
        order: {
            by: "ROUND_START_AT",
            direction: "DESC",
        },
    });

    if (loading) {
        return <FullPageSpinner />;
    }

    if (error) {
        return <div className="mb-4 w-full space-y-4 py-4">Error {error.message}</div>;
    }

    return (
        <Tabs defaultValue="all" className="m-4">
            <TabsContent value="all">
                <GamesTable gamesFragment={games} loading={loading} hasMore={hasMore} />
            </TabsContent>
        </Tabs>
    );
};

export default GamesRoute;

const GameTable_Game = graphql(`
    fragment GameTable_Game on Game {
        id
        result
        whitePlayer {
            id
            nickname
        }
        blackPlayer {
            id
            nickname
        }
        trial {
            id
            round {
                id
                scheduledStartAt
                title

                tournamentStage {
                    id
                    tournament {
                        id
                        shortTitle
                    }
                }
            }
        }
    }
`);

export function GamesTable({
    gamesFragment,
    loading,
    hasMore,
}: {
    gamesFragment: FragmentType<typeof GameTable_Game>[];
    loading: boolean;
    hasMore: boolean;
}) {
    const games = getFragmentData(GameTable_Game, gamesFragment);
    const navigate = useNavigate();
    const params = useGamesParams();

    const gamesListRef = useRef<HTMLTableSectionElement>(null);

    const scrollToTop = (ref: RefObject<HTMLTableSectionElement>) => {
        ref.current?.scroll({
            top: 0,
        });
    };

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const todaysGames = games.filter(game => {
        const gameDate = new Date(game.trial.round.scheduledStartAt);
        return gameDate >= today && gameDate < tomorrow;
    });

    const otherGames = games.filter(game => {
        const gameDate = new Date(game.trial.round.scheduledStartAt);
        return gameDate < today || gameDate >= tomorrow;
    });

    const prevButtonDisabled = loading || params.pageNumber === 0;
    const nextButtonDisabled = loading || !hasMore;

    const renderGameRows = (gamesToRender: typeof games) => {
        return gamesToRender.map(game => (
            <TableRow
                className="hover:bg-brand-purple cursor-pointer hover:bg-opacity-20"
                key={game.id}
                onClick={() => navigate(`/games/${game.id}`)}
            >
                <TableCell className="font-bold">
                    {game.trial.round.tournamentStage.tournament.shortTitle}
                </TableCell>
                <TableCell className="font-bold">{game.trial.round.title}</TableCell>
                <TableCell className="font-bold">{game.whitePlayer.nickname}</TableCell>
                <TableCell className="font-bold">{game.blackPlayer.nickname}</TableCell>
                <TableCell className="text-brand-black/60 text-sm font-normal">
                    {new Date(game.trial.round.scheduledStartAt).toLocaleString()}
                </TableCell>
                <TableCell className="font-bold">
                    <Badge
                        className={`${game.result?.toLowerCase() == "black" ? "bg-brand-black text-brand-white shadow-md" : game.result?.toLowerCase() == "white" ? "bg-brand-white shadow-md" : "bg-brand-gray-light/40 text-brand-black shadow-md"}`}
                        variant="secondary"
                    >
                        {game.result}
                    </Badge>
                </TableCell>
            </TableRow>
        ));
    };

    return (
        <Card className="bg-brand-white text-brand-black rounded border-none">
            <CardHeader>
                <CardTitle>Games</CardTitle>
            </CardHeader>
            <CardContent>
                {todaysGames.length > 0 && (
                    <>
                        <div className="mb-4">
                            <h3 className="text-brand-black mb-2 text-lg font-semibold">
                                Today's games
                            </h3>
                            <Table>
                                <TableHeader>
                                    <TableRow className="text-sm uppercase">
                                        <TableHead className="text-brand-gray-dark">
                                            Tournament
                                        </TableHead>
                                        <TableHead className="text-brand-gray-dark">
                                            Round title
                                        </TableHead>
                                        <TableHead className="text-brand-gray-dark">
                                            White
                                        </TableHead>
                                        <TableHead className="text-brand-gray-dark">
                                            Black
                                        </TableHead>
                                        <TableHead className="text-brand-gray-dark">
                                            Scheduled start at
                                        </TableHead>
                                        <TableHead className="hidden md:table-cell">
                                            Result
                                        </TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>{renderGameRows(todaysGames)}</TableBody>
                            </Table>
                        </div>
                    </>
                )}

                {otherGames.length > 0 && (
                    <>
                        <div className={todaysGames.length > 0 ? "mt-8" : ""}>
                            <h3 className="text-brand-black mb-2 text-lg font-semibold">
                                Other games
                            </h3>
                            <Table>
                                <TableHeader>
                                    <TableRow className="text-sm uppercase">
                                        <TableHead className="text-brand-gray-dark">
                                            Tournament
                                        </TableHead>
                                        <TableHead className="text-brand-gray-dark">
                                            Round title
                                        </TableHead>
                                        <TableHead className="text-brand-gray-dark">
                                            White
                                        </TableHead>
                                        <TableHead className="text-brand-gray-dark">
                                            Black
                                        </TableHead>
                                        <TableHead className="text-brand-gray-dark">
                                            Scheduled start at
                                        </TableHead>
                                        <TableHead className="hidden md:table-cell">
                                            Result
                                        </TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody ref={gamesListRef}>
                                    {renderGameRows(otherGames)}
                                </TableBody>
                            </Table>
                        </div>
                    </>
                )}
            </CardContent>
            <Pagination className="my-4">
                <PaginationContent>
                    {!prevButtonDisabled && (
                        <>
                            <PaginationItem
                                onClick={() => {
                                    scrollToTop(gamesListRef);
                                    params.setPageNumber(Math.max(0, params.pageNumber - 1));
                                }}
                            >
                                <PaginationPrevious />
                            </PaginationItem>

                            <PaginationItem
                                onClick={() => {
                                    scrollToTop(gamesListRef);
                                    params.setPageNumber(Math.max(0, params.pageNumber - 1));
                                }}
                            >
                                <PaginationLink>{params.pageNumber}</PaginationLink>
                            </PaginationItem>
                        </>
                    )}
                    <PaginationItem>
                        <PaginationLink href="#" isActive>
                            {params.pageNumber + 1}
                        </PaginationLink>
                    </PaginationItem>
                    <PaginationItem
                        onClick={() => {
                            scrollToTop(gamesListRef);
                            params.setPageNumber(params.pageNumber + 1);
                        }}
                    >
                        {!nextButtonDisabled && (
                            <PaginationLink href="#">{params.pageNumber + 2}</PaginationLink>
                        )}
                    </PaginationItem>
                    {!nextButtonDisabled && (
                        <>
                            <PaginationItem>
                                <PaginationEllipsis />
                            </PaginationItem>
                            <PaginationItem
                                onClick={() => {
                                    scrollToTop(gamesListRef);
                                    params.setPageNumber(params.pageNumber + 1);
                                }}
                            >
                                <PaginationNext />
                            </PaginationItem>
                        </>
                    )}
                </PaginationContent>
            </Pagination>
        </Card>
    );
}
